import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {BehaviorSubject, throwError} from "rxjs";
import {catchError, map} from "rxjs/operators";

import * as moment from "moment";
import {IGuest, IGuestResponse, IGuestSingleResponse} from "../_models/guest/IGuest";
import {environment} from "../../environments/environment";
import HttpStatusCode from "../_enums/HttpStatusCode";
import {CookieService} from "ngx-cookie-service";
import {IGuestAuthentication, IGuestAuthenticationSingleResponse} from "../_models/guest/IGuestAuthentication";
import {IMedicalInformation} from "../_models/medicalInformation/IMedicalInformation";
import {IMedicalInformationDetails} from "../_models/medicalInformation/IMedicalInformationDetails";
import {IMedicalRecord, IMedicalRecordDetails} from "../_models/medicalRecord/IMedicalRecord";
import {IDocumentTypeMapping} from "../_enums/IDocumentType.mapping";
import {IPrescriptionDrug} from "../_models/medicalRecord/IPrescriptionDrug";
import {IMedicalProfilePatient} from "../_models/medicalProfile/IMedicalProfilePatient";
import {IMedicalProfile} from "../_models/medicalProfile/IMedicalProfile";
import {IMedicalInformationType} from "../_models/medicalInformation/IMedicalInformationType";
import {Router} from "@angular/router";
import {IAttachment} from "../_models/medicalRecord/IAttachment";

@Injectable({
  providedIn: 'root'
})
export class GuestService {
  guest = new BehaviorSubject<IGuestAuthentication>(null);

  constructor(private http: HttpClient,
              private cookieService: CookieService,
              private router: Router) {
  }

  getGuest(id: number) {
    return this.http
      .get(environment.API_URL + "guests/" + id)
      .pipe(
        map((res: IGuestSingleResponse) => {
          return res.data;
        })
      );
  }

  getGuests() {
    return this.http
      .get(environment.API_URL + "guests/myguests")
      .pipe(
        map((res: IGuestResponse) => {
          return res.data.map(item => {
            return {
              id: item.id,
              patientAccountID: item.patientAccountID,
              email: item.email,
              description: item.description,
              token: item.token,
              password: item.password,
              permissionEndDate: item.permissionEndDate,
              active: moment(item.permissionEndDate).startOf("day").add(1, "day") > moment()
            };
          });
        })
      );
  }

  addGuest(data: IGuest) {
    return this.http.post(environment.API_URL + "guests", data).pipe(
      map((res: IGuestSingleResponse) => {
        return res.data;
      })
    );
  }

  updateGuest(data: IGuest) {
    return this.http.put(environment.API_URL + "guests/" + data.id, data).pipe(
      map((res: IGuestSingleResponse) => {
        return res.data;
      })
    );
  }

  deleteGuest(id: number) {
    return this.http
      .delete(environment.API_URL + "guests/" + id, {
        observe: "response",
      })
      .pipe(
        map((res) => {
          return res.status === HttpStatusCode.OK;
        })
      );
  }

  getMedicalInformations() {
    return this.http
      .get(environment.API_URL + "guests/medicalInformations")
      .pipe(
        map((res: IMedicalInformation[]) => {
          return res;
        })
      );
  }

  getMedicalInformationDetails(id: number) {
    return this.http
      .get(environment.API_URL + "guests/medicalInformations/" + id)
      .pipe(
        map((res: IMedicalInformationDetails) => {
          return res;
        })
      );
  }

  getFileContent(id: number) {
    return this.http
      .get(environment.API_URL + "guests/medicalInformations/files/" + id, {
        responseType: "blob" as "json",
      })
      .pipe(
        map((res) => {
          return res;
        })
      );
  }

  getMedicalRecords(startDate: string) {
    const parameters = new HttpParams()
      .set("startDate", startDate.toString());

    return this.http
      .get(environment.API_URL + "guests/medicalRecords", {params: parameters})
      .pipe(
        map((data: IMedicalRecord[]) => {
          data.forEach(item => {
            if (item.documentTypeID === IDocumentTypeMapping.CertificateMedicale) {
              // intrval normal
              item.periodDays = 0;
              const split = item.period.split("-");
              if (split.length > 0) {
                const sd = moment(split[0], "DD.MM.YYYY");
                const ed = moment(split[1], "DD.MM.YYYY");
                item.periodDays = ed.diff(sd, "days") + 1;
              }

              // interval refacere
              item.periodRestorationDays = 0;
              if (item.periodRestoration !== '') {
                const splitRestoration = item.periodRestoration.split("-");
                if (split.length > 0) {
                  const sd = moment(splitRestoration[0], "DD.MM.YYYY");
                  const ed = moment(splitRestoration[1], "DD.MM.YYYY");
                  item.periodRestorationDays = ed.diff(sd, "days") + 1;
                }
              }
            }
          });

          return data.filter((x) => x.documentTypeID > 0);
        })
      );
  }

  getMedicalRecordDetails(
    documentID: number,
    organizationID: number,
    documentTypeID: number
  ) {
    const parameters = new HttpParams()
      .set("documentID", documentID.toString())
      .set("organizationID", organizationID.toString())
      .set("documentTypeID", documentTypeID.toString());

    return this.http
      .get(environment.API_URL + "guests/medicalRecords/details", {
        params: parameters,
      })
      .pipe(
        map((data: IMedicalRecordDetails) => {
          return data;
        })
      );
  }

  getMedicalDocumetPdf(
    documentID: number,
    organizationID: number,
    documentTypeID: number,
    noLoader: boolean = false,
    isMedicalReport: boolean = false,
    clientID: number = 0,
    profileID: number = 0
  ) {
    const parameters = new HttpParams()
      .set("documentID", documentID.toString())
      .set("organizationID", organizationID.toString())
      .set("documentTypeID", documentTypeID.toString())
      .set("noLoader", noLoader ? "true" : "false")
      .set("isMedicalReport", isMedicalReport ? "true" : "false")
      .set("clientID", clientID.toString())
      .set("profileID", profileID.toString());

    return this.http
      .get(environment.API_URL + "guests/medicalRecords/Pdf", {
        params: parameters,
        responseType: "text",
      })
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getPrescriptionDrugs(organizationID: number, prescriptionID: number) {
    const parameters = new HttpParams()
      .append("organizationID", organizationID.toString())
      .append("prescriptionID", prescriptionID.toString());

    return this.http.get(environment.API_URL + "guests/medicalRecords/prescriptionDrugs", {params: parameters}).pipe(
      map((data: IPrescriptionDrug[]) => {
        return data;
      })
    );
  }

  getPatientDetails() {
    return this.http
      .get(environment.API_URL + "guests/medicalProfiles/personalDetails")
      .pipe(
        map(
          (res: IMedicalProfilePatient) => {
            return res;
          }
        )
      );
  }

  getMedicalProfileDetails() {
    return this.http.get(environment.API_URL + "guests/medicalProfiles").pipe(
      map(
        (res: IMedicalProfile) => {
          return res;
        }
      )
    );
  }

  getMedicalInformationTypes() {
    return this.http
      .get(environment.API_URL + "guests/medicalInformations/types")
      .pipe(
        map((res: IMedicalInformationType[]) => {
          return res;
        })
      );
  }

  getAttachments(organizationID: number, documentID: number) {
    const parameters = new HttpParams()
      .append("organizationID", organizationID.toString())
      .append("documentID", documentID.toString());

    return this.http.get(environment.API_URL + "guests/medicalRecords/GetAttachedFiles", {params: parameters}).pipe(
      map((data: IAttachment[]) => {
        return data;
      })
    );
  }

  getAttachmentPdf(
    fileID: number,
    organizationID: number,
    noLoader: boolean = false
  ) {
    const parameters = new HttpParams()
      .set("fileID", fileID.toString())
      .set("organizationID", organizationID.toString())
      .set("noLoader", noLoader ? "true" : "false");

    return this.http
      .get(environment.API_URL + "guests/medicalRecords/GetFileAsByteArray", {
        params: parameters,
        responseType: "text",
      })
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  login(token: string, password: string) {
    return this.http.post(environment.API_URL + "guests/Authenticate", {token, password}).pipe(
      map((res: IGuestAuthenticationSingleResponse) => {
        this.guest.next(res.data);

        this.cookieService.set("guestToken", res.data.token, {
          path: "/",
        });

        return res.data;
      }),
      catchError((error) => {
        if (typeof (error) === 'string') {
          return throwError(error);
        }

        let errorMessage = "";
        if (error.error.errors) {
          const errors = error.error.errors;

          if (typeof errors === "object") {
            const errorsList = [];
            for (const prop in errors) {
              if (errors.hasOwnProperty(prop)) {
                errorsList.push(errors[prop]);
              }
            }

            errorMessage = errorsList.join("<br />");
          } else {
            errorMessage = errors;
          }
        } else {
          errorMessage = error.error.message;
        }

        return throwError(errorMessage);
      })
    );
  }

  getAuthenticationDetails() {
    return this.http
      .get(environment.API_URL + "guests/authenticateResponse")
      .pipe(
        map((res: IGuestAuthenticationSingleResponse) => {
          return res.data;
        })
      );
  }

  autoLogin() {
    const guestToken = this.getToken();

    if (guestToken) {
      this.getAuthenticationDetails().subscribe(
        (res: IGuestAuthentication) => {
          this.guest.next(res);
        }
      );
    }
  }

  logout(errorMessage = "") {
    this.cookieService.delete("guestToken", "/");
    this.router.navigate(['/authentication/signinguest'], {state: {message: errorMessage}});
  }

  getToken() {
    if (this.cookieService.get("guestToken") !== "") {
      return this.cookieService.get("guestToken");
    }

    return null;
  }
}
